import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import publicApi from '../../utils/publicApi';
import {
  safeSessionGetItem,
  safeSessionSetItem,
} from '../../shared/safeStorage';
import {
  persistSelfReportedEmail,
  getSelfReportedEmail,
} from '../../shared/selfReportedEmail';

// THT-609 — Demo email capture gate.
//
// After a short tease (the visitor sees the live demo for ~1.5s), this renders
// a full-viewport backdrop-blur overlay above the sticky banner with a small,
// MANDATORY centered email card. There is no close button, no Esc, and no
// backdrop dismiss — the demo stays blurred until a valid email is submitted.
//
// On submit we POST to the public, token-less `/highway/demo-lead` endpoint.
// The gate is intentionally FAIL-OPEN: a backend error still unblurs the demo
// so we never trap a visitor behind an API failure. It submits at most once
// per browser session (sessionStorage flag), so navigating within the demo
// doesn't re-trigger it. And once we've captured a valid email at all — this
// visit, a prior visit, or the marketing-site prequal — the gate never opens
// again: there's nothing left to ask for (see `getSelfReportedEmail`, which
// reads the 30-day cookie + localStorage the submit persists below).
//
// On submit we also persist the email via persistSelfReportedEmail (the shared
// THT-611 helper): it writes a `.tachusa.com`-scoped cookie + localStorage so
// the marketing-site prequal flow and the Auth0 signup box can prefill
// `login_hint` later. This runs regardless of the POST outcome — the captured
// email is useful for prefill even if the lead call failed.
//
// PARTNER BYPASS: a `?skipEmail=true` query param suppresses the gate entirely.
// We share that link with partners so they can explore the demo without being
// prompted as if they were a customer lead. It's intentionally trivial to defeat
// (anyone reading the source can add the param) — the goal is to not nag
// partners, not to lock customers in.

const TEASE_MS = 1500;
const SESSION_FLAG = 'demoEmailGateSubmitted';
// Partner share-link bypass. Any truthy-ish value enables it (`true`, `1`, bare
// `?skipEmail`); only an explicit `false`/`0` is treated as "don't skip".
const SKIP_URL_PARAM = 'skipEmail';
const SKIP_SESSION_FLAG = 'demoEmailGateSkipped';
const DEMO_LEAD_PATH = '/highway/demo-lead';
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Bound the request so a hung connection can't trap the visitor: without this
// axios waits forever (default timeout 0), the spinner never stops, and the
// mandatory gate (no Esc/close) stays up — defeating the FAIL-OPEN intent. On
// timeout axios rejects, the catch fires, and `finally` unblurs.
const REQUEST_TIMEOUT_MS = 10000;

const alreadySubmittedThisSession = (): boolean => {
  return safeSessionGetItem(SESSION_FLAG) === 'true';
};

// Cross-session suppression: once a valid self-reported email is on file (this
// gate on a prior visit, or the marketing-site prequal — both write the shared
// `.tachusa.com` cookie + localStorage), never prompt again. `sessionStorage`
// alone only covers the current tab/session; this is what makes "enter it once,
// never asked again" hold across visits.
const alreadyCapturedEmail = (): boolean => Boolean(getSelfReportedEmail());

const markSubmittedThisSession = () => {
  // sessionStorage may be unavailable (private mode); the in-memory state
  // already prevents a re-open for the life of this component.
  safeSessionSetItem(SESSION_FLAG, 'true');
};

// True when the visitor arrived via a partner share link (`?skipEmail=true`) or
// already skipped earlier this session. We persist the URL skip to sessionStorage
// so a hard reload on an inner demo route — where the query string is gone, since
// in-demo navigation doesn't carry it — keeps the gate suppressed all session.
const shouldSkipViaUrl = (): boolean => {
  // Unavailable storage returns null here, which is not 'true', so this falls
  // through to the URL check below exactly as the old catch did.
  if (safeSessionGetItem(SKIP_SESSION_FLAG) === 'true') return true;

  let skip = false;
  try {
    const value = new URLSearchParams(window.location.search).get(
      SKIP_URL_PARAM
    );
    skip = value !== null && value !== 'false' && value !== '0';
  } catch {
    skip = false;
  }

  if (skip) {
    // Can't persist; the in-URL param still suppresses the gate on this load.
    safeSessionSetItem(SKIP_SESSION_FLAG, 'true');
  }
  return skip;
};

function DemoEmailGate() {
  const [gateOpen, setGateOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  // Tease, then open — unless we already have this visitor's email (this session
  // or any prior one), or they arrived via a partner bypass link
  // (`?skipEmail=true`).
  useEffect(() => {
    if (
      alreadySubmittedThisSession() ||
      alreadyCapturedEmail() ||
      shouldSkipViaUrl()
    )
      return;
    const timer = setTimeout(() => setGateOpen(true), TEASE_MS);
    return () => clearTimeout(timer);
  }, []);

  // Move focus into the card when it opens (accessibility + focus-trap start).
  useEffect(() => {
    if (gateOpen) inputRef.current?.focus();
  }, [gateOpen]);

  // Lock body scroll while the gate is open so the blurred dashboard behind the
  // modal can't be two-finger/scroll-wheel scrolled. Restore the prior value on
  // close/unmount (don't hardcode '' — respect whatever was set before us).
  useEffect(() => {
    if (!gateOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [gateOpen]);

  const closeGate = () => {
    markSubmittedThisSession();
    setGateOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    const trimmed = email.trim();
    if (!EMAIL_REGEX.test(trimmed)) {
      setError('Please enter a valid email address.');
      inputRef.current?.focus();
      return;
    }

    setError('');
    setSubmitting(true);

    try {
      // Persist for `login_hint` prefill BEFORE the network call so the cookie +
      // localStorage are written regardless of the POST outcome — the captured
      // email is useful for the prequal/signup prefill even if the lead fails.
      // Kept inside the try so a storage throw (Safari private mode / disabled
      // localStorage) still hits the fail-open catch/finally below rather than
      // hanging the spinner on the mandatory gate.
      persistSelfReportedEmail(trimmed);

      await publicApi.post(
        DEMO_LEAD_PATH,
        { email: trimmed },
        { timeout: REQUEST_TIMEOUT_MS }
      );
    } catch (err) {
      // FAIL-OPEN: never trap the visitor behind a backend error — but don't
      // swallow it silently. A failure here is often a request that never
      // reached the BE (network/CORS/timeout), so log it for visibility. We
      // still let them through.
      console.error('Demo email-gate lead submission failed', err);
      Sentry.captureException(err, {
        tags: { demoEmailGate: 'submit-failed' },
      });
    } finally {
      setSubmitting(false);
      closeGate();
    }
  };

  // Focus-trap: keep Tab / Shift+Tab cycling between the focusable controls.
  // While submitting, the button is `disabled` and drops out of the tab order,
  // so the input is then both the first and last stop — otherwise forward-Tab
  // would skip the disabled button and escape the mandatory modal.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const first = inputRef.current;
    if (!first) return;
    const submit = submitRef.current;
    const last = submit && !submit.disabled ? submit : first;

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  if (!gateOpen) return null;

  return (
    <div
      // Full-viewport blur ABOVE the sticky banner (z-50). Pointer events land
      // on this overlay, blocking interaction with the demo underneath.
      className="fixed inset-0 z-[100] flex items-center justify-center bg-white/30 p-4 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-labelledby="demo-email-gate-heading"
      onKeyDown={handleKeyDown}
      data-testid="demo-email-gate"
    >
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl">
        <h2
          id="demo-email-gate-heading"
          className="mb-4 text-lg font-semibold text-gray-900"
        >
          Please enter your email to continue exploring TACH
        </h2>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <label htmlFor="demo-email-input" className="sr-only">
            Email address
          </label>
          <input
            id="demo-email-input"
            ref={inputRef}
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              // Clear the stale error (and aria-invalid) as soon as they edit.
              if (error) setError('');
            }}
            placeholder="you@company.com"
            autoComplete="email"
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? 'demo-email-error' : undefined}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-ctadarkgreen focus:outline-none focus:ring-1 focus:ring-ctadarkgreen"
          />
          {error && (
            <p id="demo-email-error" className="text-left text-xs text-red-600">
              {error}
            </p>
          )}
          <button
            ref={submitRef}
            type="submit"
            disabled={submitting}
            className="flex items-center justify-center rounded-full bg-ctadarkgreen px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? (
              <span
                aria-label="Submitting"
                role="status"
                className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
              />
            ) : (
              'Explore TACH'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default DemoEmailGate;
